const STORAGE_KEY = "TradeAppErrorLog_v1";
const MAX_ENTRIES = 200;
const CONSOLE_PREFIX = "[TradeAppErrorLog]";
const IS_DEV = process.env.NODE_ENV === "development";

function getStore() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : sessionStorage;
  } catch {
    return null;
  }
}

function readEntries() {
  const store = getStore();
  if (!store) return [];
  try {
    let raw = store.getItem(STORAGE_KEY);
    if (!raw && store === localStorage && typeof sessionStorage !== "undefined") {
      raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        store.setItem(STORAGE_KEY, raw);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeEntries(entries) {
  const store = getStore();
  if (!store) return;
  const trimmed = entries.slice(-MAX_ENTRIES);
  const payload = JSON.stringify(trimmed);
  try {
    store.setItem(STORAGE_KEY, payload);
  } catch {
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-Math.floor(MAX_ENTRIES / 2))));
    } catch {
      /* quota */
    }
  }
}

function detailsOrOmit(details) {
  if (!details || typeof details !== "object") return {};
  const keys = Object.keys(details);
  return keys.length ? { details } : {};
}

function mirrorEntryToLocalFile(entry) {
  const url = process.env.REACT_APP_ERROR_LOG_URL;
  if (!url || typeof fetch === "undefined") return;
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(entry),
    keepalive: true,
  }).catch(() => {});
}

function rejectionMessage(reason) {
  if (reason instanceof Error) return reason.message;
  if (reason && typeof reason === "object" && "message" in reason) {
    return String(reason.message);
  }
  return String(reason);
}

/**
 * @param {{ type?: string, message: string, details?: Record<string, unknown> }} payload
 */
export function logError(payload) {
  const entry = {
    t: new Date().toISOString(),
    type: payload.type || "app",
    message: payload.message,
    ...detailsOrOmit(payload.details),
  };
  const next = readEntries();
  next.push(entry);
  writeEntries(next);
  mirrorEntryToLocalFile(entry);
  if (IS_DEV) {
    console.warn(CONSOLE_PREFIX, entry);
  }
}

function isAxiosCanceled(error) {
  return (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.message === "canceled"
  );
}

function apiErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "object" && data !== null && data.message) {
    return String(data.message);
  }
  if (typeof data === "string") return data;
  return error?.message || "HTTP request failed";
}

/**
 * @param {import("axios").AxiosError} error
 * @param {Record<string, unknown>} [extra]
 */
export function logAxiosError(error, extra = {}) {
  if (isAxiosCanceled(error)) return;
  const cfg = error?.config || {};
  logError({
    type: "api",
    message: apiErrorMessage(error),
    details: {
      status: error?.response?.status,
      method: cfg.method,
      url: cfg.url,
      baseURL: cfg.baseURL,
      code: error?.code,
      ...extra,
    },
  });
}

/**
 * @param {unknown} error
 * @param {Record<string, unknown>} [extra]
 */
export function logRuntimeError(error, extra = {}) {
  const err = error instanceof Error ? error : new Error(String(error));
  logError({
    type: "runtime",
    message: err.message,
    details: {
      stack: err.stack,
      ...extra,
    },
  });
}

export function getErrorLog() {
  return readEntries();
}

export function clearErrorLog() {
  const store = getStore();
  if (store) store.removeItem(STORAGE_KEY);
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function downloadErrorLog() {
  const data = JSON.stringify(readEntries(), null, 2);
  const blob = new Blob([data], { type: "application/json" });
  const a = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);
  a.href = objectUrl;
  a.download = `trade-app-error-log-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

let globalHandlersInstalled = false;

export function initGlobalErrorLogging() {
  if (typeof window === "undefined" || globalHandlersInstalled) return;
  globalHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    logError({
      type: "window",
      message: event.message || "window.onerror",
      details: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    logError({
      type: "unhandledrejection",
      message: rejectionMessage(reason) || "Unhandled promise rejection",
      details: {
        stack: reason instanceof Error ? reason.stack : undefined,
      },
    });
  });

  if (IS_DEV) {
    window.__tradeAppErrorLog = {
      get: getErrorLog,
      clear: clearErrorLog,
      download: downloadErrorLog,
    };
  }
}
